import type {
  YuzuBlock,
  YuzuHandwritingInk,
  YuzuHandwritingStyle,
  YuzuTemplateSchema,
} from '@giftie/yuzu-integration'
import {
  GIFTIE_CONTENT_FONT_VARIABLE,
  isYuzuHandwritingBlock,
  readYuzuHandwritingAttributes,
  YUZU_MESSAGE_MAX_CHARS,
} from '@giftie/yuzu-integration'
import { findYuzuBlock } from './yuzuBlockTree'
import { isYuzuTextHtmlVisuallyEmpty } from './yuzuEmptyTextCaret'

/**
 * Hard ceiling for handwriting messages. Templates may ask for fewer
 * characters; every preview render is paid for, so none may ask for more.
 */
export const HANDWRITING_MESSAGE_MAX_CHARS = YUZU_MESSAGE_MAX_CHARS

/** Base ink colours of Yuzu's handwriting SVG filters, used for the text view while typing. */
export const HANDWRITING_INK_HEX: Record<YuzuHandwritingInk, string> = {
  black: '#08040A',
  blue: '#3366FF',
  darkBlue: '#373DCF',
}

/**
 * The text view types in the store's Content font, which `app.vue` sets as a
 * CSS variable on the app root (`createGiftieContentFontVariables`). A store
 * without one inherits the builder's font, like Yuzu text blocks that use the
 * variable. The preview and the printed card use the handwriting style.
 */
export const HANDWRITING_TYPING_FONT_FAMILY = `var(${GIFTIE_CONTENT_FONT_VARIABLE})`

export const HANDWRITING_PREVIEW_FAILED_NOTE =
  'We could not preview your handwriting just now. Your message is still saved with the card.'

/** Pre-rendered style/ink sample served from the shared public assets. */
export function getHandwritingSampleUrl(
  style: YuzuHandwritingStyle,
  ink: YuzuHandwritingInk,
) {
  return `/handwriting/${style}-${ink}-message.png`
}

/** The smallest of the template's `maxChars`, the builder's limit and the hard ceiling. */
export function getHandwritingCharacterLimit(
  block: YuzuBlock,
  builderLimit = Infinity,
) {
  const maxChars = readYuzuHandwritingAttributes(block)?.maxChars
  return Math.min(
    maxChars ?? HANDWRITING_MESSAGE_MAX_CHARS,
    HANDWRITING_MESSAGE_MAX_CHARS,
    builderLimit,
  )
}

function unescapeHtml(html: string) {
  return html
    .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
    .replaceAll('&amp;', '&')
}

/**
 * Serializes shopper text the way Yuzu's handwriting renderer and the
 * corporate template placeholder store it: one `<p>` per line, blank lines
 * as empty paragraphs. Whitespace-only text becomes an empty block. Shared
 * with the Next app, which writes imported orders' gift messages into
 * company cards the same way.
 */
export { createYuzuMessageHtml as createHandwritingHtml } from '@giftie/yuzu-integration'

/**
 * Recovers plain text from stored handwriting HTML: Yuzu paragraphs, editor
 * `<div>` blocks, `<br>` line breaks or legacy plain text.
 */
export function readHandwritingText(html: string) {
  return unescapeHtml(
    html
      .replace(/\r\n?/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(?:p|div|li)>\s*(?=<(?:p|div|li)[\s>])/gi, '\n')
      .replace(/<[^>]*>/g, ''),
  )
    .replaceAll('​', '')
    .trim()
}

function readFirstChildHtml(block: YuzuBlock | null) {
  return typeof block?.children[0] === 'string' ? block.children[0] : ''
}

/**
 * A handwriting block is untouched while it still shows the template's own
 * copy (corporate designs seed a visible placeholder) or nothing at all.
 * Untouched blocks show the style sample and save as empty.
 */
export function isYuzuHandwritingUntouched(
  html: string,
  sourceHtml: string | null,
) {
  return isYuzuTextHtmlVisuallyEmpty(html) || html === sourceHtml
}

/** The message HTML a block contributes to the session; untouched handwriting is empty. */
export function readYuzuMessageHtml(
  block: YuzuBlock | null,
  sourceSchema: YuzuTemplateSchema | undefined,
) {
  const html = readFirstChildHtml(block)
  if (!block || !isYuzuHandwritingBlock(block)) return html
  const sourceBlock = findYuzuBlock(
    sourceSchema?.pages.flatMap((page) => page.blocks) ?? [],
    block.id,
  )
  return isYuzuHandwritingUntouched(
    html,
    sourceBlock ? readFirstChildHtml(sourceBlock) : null,
  )
    ? ''
    : html
}

function clearUntouchedBlock(
  block: YuzuBlock,
  sourceSchema: YuzuTemplateSchema,
): YuzuBlock {
  const children = block.children.map((child) =>
    typeof child === 'object'
      ? clearUntouchedBlock(child, sourceSchema)
      : child,
  )
  if (!isYuzuHandwritingBlock(block)) return { ...block, children }
  const html = readYuzuMessageHtml(block, sourceSchema)
  return { ...block, children: [html, ...children.slice(1)] }
}

/**
 * Saves untouched handwriting blocks as empty, like ordinary text blocks,
 * instead of persisting the template placeholder as the shopper's message.
 * The live builder state is left alone so the editor keeps showing the sample.
 */
export function clearUntouchedYuzuHandwriting(
  schema: YuzuTemplateSchema,
  sourceSchema: YuzuTemplateSchema,
): YuzuTemplateSchema {
  return {
    ...schema,
    pages: schema.pages.map((page) => ({
      ...page,
      blocks: page.blocks.map((block) =>
        clearUntouchedBlock(block, sourceSchema),
      ),
    })),
  }
}

/** Cache key for one rendered preview; style, ink and geometry are fixed per block. */
export function getHandwritingPreviewKey(blockId: string, text: string) {
  return `${blockId}\u0000${text}`
}

export type HandwritingViewMode = 'sample' | 'text' | 'rendering' | 'image'

export type HandwritingViewState = {
  mode: HandwritingViewMode
  note: string | null
}

export type HandwritingViewEvent =
  | { type: 'edit' }
  | {
      type: 'settle'
      text: string
      untouched: boolean
      hasPreview: boolean
    }
  | { type: 'rendered' }
  | { type: 'failed'; note: string }

export const INITIAL_HANDWRITING_VIEW: HandwritingViewState = {
  mode: 'sample',
  note: null,
}

/**
 * The handwriting block's view state machine.
 *
 * - `edit` always opens the text view.
 * - `settle` (mount, blur, "Preview handwriting", external content change)
 *   shows the sample for an untouched block, the text view for an empty
 *   one, the cached preview when there is one and otherwise renders.
 * - `rendered`/`failed` only apply while rendering, so a late response
 *   never pulls the shopper out of the editor.
 */
export function reduceHandwritingView(
  state: HandwritingViewState,
  event: HandwritingViewEvent,
): HandwritingViewState {
  switch (event.type) {
    case 'edit':
      return { mode: 'text', note: null }
    case 'settle':
      if (event.text === '') {
        return { mode: event.untouched ? 'sample' : 'text', note: null }
      }
      return { mode: event.hasPreview ? 'image' : 'rendering', note: null }
    case 'rendered':
      return state.mode === 'rendering' ? { mode: 'image', note: null } : state
    case 'failed':
      return state.mode === 'rendering'
        ? { mode: 'text', note: event.note }
        : state
  }
}
